import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { MdDelete } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { BASEAPIURL, token } from "../../env";

export default function Event() {
  const initialData = {
    EvtID: "",
    EvtTitle: "",
    EvtDesc: "",
    EvtImages: "",
    EvtFromDate: "",
    EvtToDate: "",
    EvtDeActive: "",
  };

  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(initialData);
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    setFilteredEvents(
      searchTerm
        ? events.filter((evt) =>
            evt.EvtTitle.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : events
    );
  }, [searchTerm, events]);

  const fetchEvents = async () => {
    try {
      const url = `${BASEAPIURL}/EventList?Token=${encodeURIComponent(
        token
      )}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = await res.json();

      if (Array.isArray(result?.data)) {
        const parsedData = result.data.map((evt) => ({
          ...evt,
          EvtID: Number(evt.EvtID || evt.EvtId || evt.EventId || evt.id || 0),
          EvtImages: evt.EvtImages ? safeJsonParse(evt.EvtImages, []) : [],
        }));

        setEvents(parsedData);
      } else {
        setEvents([]);
      }
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setEvents([]);
    }
  };

  function safeJsonParse(str, fallback) {
    try {
      return JSON.parse(str);
    } catch {
      return fallback;
    }
  }

  const handleShowForm = (event = null) => {
    if (event) {
      setIsEditMode(true);
      const imagesArray = Array.isArray(event.EvtImages)
        ? event.EvtImages
        : safeJsonParse(event.EvtImages, []);
      setFormData({
        EvtID: Number(event.EvtID),
        EvtTitle: event.EvtTitle,
        EvtDesc: event.EvtDesc,
        EvtFromDate: event.EvtFromDate?.slice(0, 10),
        EvtToDate: event.EvtToDate?.slice(0, 10),
        EvtImages: JSON.stringify(imagesArray),
        EvtDeActive: Boolean(event.EvtDeActive),
      });
      setSelectedImages(imagesArray);
    } else {
      setIsEditMode(false);
      setFormData(initialData);
      setSelectedImages([]);
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setFormData(initialData);
    setSelectedImages([]);
    setIsEditMode(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImages((prev) => [
          ...prev,
          { base64: reader.result, name: file.name },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    const updatedImages = selectedImages.filter((_, i) => i !== index);
    setSelectedImages(updatedImages);
  };

  const uploadImagesToServer = async () => {
    const alreadyUploaded = selectedImages.filter(
      (img) => img.url && !img.base64?.startsWith("data:")
    );

    const newImages = selectedImages.filter((img) =>
      img.base64?.startsWith("data:")
    );

    if (newImages.length === 0) {
      return alreadyUploaded;
    }

    const uploadData = new FormData();

    newImages.forEach((imgData, index) => {
      const blob = dataURLtoBlob(imgData.base64);
      const mime = imgData.base64.match(/data:(.*?);/)[1] || "image/png";
      const ext = mime.split("/")[1] || "png";
      const filename = `event_${Date.now()}_${index}.${ext}`;
      const file = new File([blob], filename, { type: mime });

      uploadData.append("EvtImages", file);
    });

    uploadData.append("token", token);
    uploadData.append("EvtTitle", formData.EvtTitle);
    uploadData.append("EvtDesc", formData.EvtDesc);
    uploadData.append("EvtFromDate", `${formData.EvtFromDate}T00:00:00`);
    uploadData.append("EvtToDate", `${formData.EvtToDate}T23:59:59`);
    uploadData.append("EvtDeActive", formData.EvtDeActive);

    try {
      const response = await fetch(`${BASEAPIURL}/UploadEvent`, {
        method: "POST",
        body: uploadData,
      });

      const result = await response.json();

      const newlyUploaded = Array.isArray(result?.data)
        ? result.data.map((filename) => ({
            name: filename,
            url: `${BASEAPIURL}/uploads/${filename}`,
          }))
        : [];

      return [...alreadyUploaded, ...newlyUploaded];
    } catch (error) {
      console.error("Upload failed:", error);
      return alreadyUploaded;
    }
  };

  function dataURLtoBlob(dataURL) {
    const [meta, base64] = dataURL.split(",");
    const mime = meta.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const array = [];

    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }

    return new Blob([new Uint8Array(array)], { type: mime });
  }

  const handleInsert = async () => {
    const isDuplicate = events.some(
      (evt) => evt.EvtTitle.toLowerCase() === formData.EvtTitle.toLowerCase()
    );
    if (isDuplicate) {
      alert("An event with this title already exists.");
      return;
    }

    const uploadedImages = await uploadImagesToServer();
    try {
      const uploadedImages = await uploadImagesToServer();
      const imageNames = (uploadedImages || [])
        .map((img) => img.name)
        .filter(Boolean); // ensure only strings
      const payload = {
        tokenData: token,
        Evt: {
          EvtID: isEditMode ? formData.EvtID : 0,
          EvtTitle: formData.EvtTitle,
          EvtDesc: formData.EvtDesc,
          EvtImages: imageNames, // ✅ valid array of strings only
          EvtFromDate: `${formData.EvtFromDate}T00:00:00`,
          EvtToDate: `${formData.EvtToDate}T23:59:59`,
          EvtDeActive: Boolean(formData.EvtDeActive), // ✅ actual boolean
        },
      };
      const payload2 = {
        tokenData: token,
        Evt: {
          EvtTitle: formData.EvtTitle,
          EvtDesc: formData.EvtDesc,
          EvtImages: imageNames, // ✅ valid array of strings only
          EvtFromDate: `${formData.EvtFromDate}T00:00:00`,
          EvtToDate: `${formData.EvtToDate}T23:59:59`,
          EvtDeActive: Boolean(formData.EvtDeActive), // ✅ actual boolean
        },
      };

      const response = await fetch(`${BASEAPIURL}/InsertEvent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload2),
      });

      const result = await response.json();
      if (result?.data === "true") {
        fetchEvents();
        handleCloseForm();
      } else {
        alert(result?.Message || "Failed to insert event.");
      }
    } catch (err) {
      console.error("Insert error:", err);
    }
  };

  const handleUpdate = async () => {
    const uploadedImages = await uploadImagesToServer();
    try {
      const uploadedImages = await uploadImagesToServer();
      const imageNames = (uploadedImages || [])
        .map((img) => img.name)
        .filter(Boolean); // ensure only strings
      const payload = {
        tokenData: token,
        Evt: {
          EvtID: isEditMode ? formData.EvtID : 0,
          EvtTitle: formData.EvtTitle,
          EvtDesc: formData.EvtDesc,
          EvtImages: imageNames, // ✅ valid array of strings only
          EvtFromDate: `${formData.EvtFromDate}T00:00:00`,
          EvtToDate: `${formData.EvtToDate}T23:59:59`,
          EvtDeActive: Boolean(formData.EvtDeActive), // ✅ actual boolean
        },
      };

      const response = await fetch(
        `${BASEAPIURL}/UpdateEvent?MainID=${formData.EvtID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (result?.data === true) {
        fetchEvents();
        handleCloseForm();
      } else {
        alert(result?.error || "Failed to update event.");
      }
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const handleSave = () => {
    if (isEditMode) {
      handleUpdate();
    } else {
      handleInsert();
    }
  };

  const handleDelete = async (eventID) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      const res = await fetch(`${BASEAPIURL}/DeleteEvent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tokenData: token, EventId: eventID }),
      });

      const result = await res.json();
      if (result?.Result === "Success") {
        fetchEvents();
      } else {
        alert(result?.Message || "Failed to delete event.");
      }
    } catch (err) {
      console.error("Error deleting event:", err);
    }
  };

  const isFormValid =
    formData.EvtTitle &&
    formData.EvtDesc &&
    formData.EvtFromDate &&
    formData.EvtToDate;

  return (
    <>
      <div
        className="container"
        style={{ overflowY: "auto", maxHeight: "80vh" }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center my-3 gap-3">
          <input
            className="form-control w-100 w-md-50"
            placeholder="Search by title"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="btn btn-dark" onClick={() => handleShowForm()}>
            + Add Event
          </button>
        </div>
        <div
          className="table-responsive"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <table className="table table-bordered ">
            <thead className="table-dark">
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>From</th>
                <th>To</th>
                <th>Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length > 0 ? (
                filteredEvents.map((evt) => (
                  <tr key={evt.EvtID}>
                    <td>{evt.EvtTitle}</td>
                    <td>{evt.EvtDesc}</td>
                    <td>{evt.EvtFromDate?.slice(0, 10)}</td>
                    <td>{evt.EvtToDate?.slice(0, 10)}</td>
                    <td>{evt.EvtDeActive ? "YES" : "NO"}</td>
                    <td>
                      <FaEdit
                        onClick={() => handleShowForm(evt)}
                        style={{ cursor: "pointer" }}
                      />
                      <MdDelete
                        onClick={() => handleDelete(evt.EvtID)}
                        style={{
                          cursor: "pointer",
                          color: "red",
                          marginLeft: 10,
                        }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No events found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal size="lg" show={showForm} onHide={handleCloseForm}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? "Edit Event" : "Add Event"}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="EvtTitle">Title</label>
              <input
                type="text"
                id="EvtTitle"
                name="EvtTitle"
                className="form-control"
                value={formData.EvtTitle}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="EvtDesc">Description</label>
              <input
                type="text"
                id="EvtDesc"
                name="EvtDesc"
                className="form-control"
                value={formData.EvtDesc}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="EvtFromDate">From Date</label>
              <input
                type="date"
                id="EvtFromDate"
                name="EvtFromDate"
                className="form-control"
                value={formData.EvtFromDate}
                onChange={handleInputChange}
                   min="1000-01-01"
                  max="9999-12-31"

              />
            </div>

            <div className="col-md-6">
              <label htmlFor="EvtToDate">To Date</label>
              <input
                type="date"
                id="EvtToDate"
                name="EvtToDate"
                className="form-control"
                value={formData.EvtToDate}
                onChange={handleInputChange}
                 min="1000-01-01"
                max="9999-12-31"
              />
            </div>

            <div className="col-12">
              <label htmlFor="EvtImages">Upload Images </label>
              <input
                type="file"
                id="EvtImages"
                name="EvtImages"
                className="form-control"
                multiple
                accept=".jpg,.jpeg,.png,.pdf,.mp4"
                onChange={handleImageChange}
              />

              {selectedImages.length > 0 && (
                <div className="mt-3 d-flex flex-wrap gap-3">
                  {selectedImages.map((img, idx) => {
                    const src = img.base64 || img.url || "";
                    const name = img.name || `File ${idx + 1}`;
                    const isVideo =
                      src.includes(".mp4") || src.includes("video");
                    const isPDF =
                      src.includes(".pdf") || src.includes("application/pdf");
                    const isImage = !isVideo && !isPDF;

                    return (
                      <div
                        key={idx}
                        style={{
                          position: "relative",
                          width: 120,
                          textAlign: "center",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          padding: 6,
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        <div
                          style={{ fontSize: "12px", marginBottom: 4 }}
                          title={name}
                        >
                          {name.length > 15 ? name.slice(0, 12) + "..." : name}
                        </div>

                        {isVideo ? (
                          <video
                            src={src}
                            controls
                            style={{ maxHeight: "80px", width: "100%" }}
                          />
                        ) : isPDF ? (
                          <a
                            href={src}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <div
                              style={{
                                height: 80,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#eee",
                                borderRadius: 4,
                                fontWeight: "bold",
                                color: "#dc3545",
                              }}
                            >
                              PDF
                            </div>
                          </a>
                        ) : (
                          <img
                            src={src}
                            alt={name}
                            style={{
                              maxHeight: "80px",
                              width: "100%",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}

                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          title="Remove"
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            backgroundColor: "#dc3545",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            fontSize: 12,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label htmlFor="EvtDeActive">Active Status</label>
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className={`form-check-input ${
                    formData.EvtDeActive ? "bg-success" : "bg-secondary"
                  }`}
                  id="EvtDeActive"
                  checked={formData.EvtDeActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      EvtDeActive: e.target.checked,
                    })
                  }
                  style={{ width: "50px" }}
                />
              </div>
            </div>
          </div>

          <div className="text-center mt-3">
            <button
              className="btn btn-dark"
              disabled={
                !formData.EvtTitle ||
                !formData.EvtDesc ||
                !formData.EvtFromDate ||
                !formData.EvtToDate
              }
              onClick={handleSave}
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
